import { getColdStartDisplay, isColdStart } from "./cold-start";
import { getHabitDayState } from "./day-state";
import { addDays } from "./day-boundary";
import { getEffectiveTarget } from "./light-day";
import { getPeriodProgress } from "./period-progress";
import { getDueDatesInRange, getPeriodBounds } from "./schedule";
import { parseScheduleConfig } from "./schedule-config";
import { computeScore } from "./score";

import type { AppSettings } from "../context/index";
import type { Category, Completion, Habit, TimeOfDay } from "../db/schema";
import type { CompletionRecord, HabitDayState, PeriodProgress, ScheduleContext } from "./types";

// ─── Types ───

export interface HabitDisplay {
  habit: Habit;
  dayState: HabitDayState;
  score: number;
  isColdStart: boolean;
  coldStartDisplay: { completedThisWeek: number; totalThisWeek: number } | null;
  periodProgress: PeriodProgress | null;
  completion: Completion | null;
  effectiveTarget: number;
  category: Category | null;
}

export interface TimeBlock {
  name: TimeOfDay;
  label: string;
  habits: HabitDisplay[];
  completed: number;
  total: number;
}

export type LayoutMode = "lean" | "dense";

export interface DailyViewData {
  blocks: TimeBlock[];
  allHabits: HabitDisplay[];
  totalDue: number;
  totalDone: number;
  layoutMode: LayoutMode;
  today: string;
  currentBlock: TimeOfDay;
  isLightDay: boolean;
}

// ─── Constants ───

const BLOCK_ORDER: TimeOfDay[] = ["morning", "afternoon", "evening", "anytime"];
const BLOCK_LABELS: Record<TimeOfDay, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  anytime: "Anytime",
};

// ─── Helpers ───

function toCompletionRecords(completions: Completion[]): CompletionRecord[] {
  return completions.map((c) => ({
    effective_date: c.effective_date,
    type: c.type,
    value: c.value,
  }));
}

export function getCurrentBlock(): TimeOfDay {
  const hour = new Date().getHours();
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

// ─── Pure computation ───

export interface ComputeDailyViewInput {
  habits: Habit[];
  completions: Completion[];
  categories: Category[];
  settings: AppSettings;
  today: string;
}

export function computeDailyView(input: ComputeDailyViewInput): DailyViewData {
  const { habits, completions, categories, settings, today } = input;
  const { firstDayOfWeek, isLightDayToday } = settings;
  const currentBlock = getCurrentBlock();

  // Build category lookup map
  const categoryMap = new Map(categories.map((c) => [c.id, c]));

  // Compute display data for each habit
  const allHabits: HabitDisplay[] = habits.map((habit) => {
    const config = parseScheduleConfig(habit.schedule_type, habit.schedule_value);
    const habitCompletions = completions.filter((c) => c.habit_id === habit.id);
    const completionRecords = toCompletionRecords(habitCompletions);

    const context: ScheduleContext = {
      habitStartDate: habit.created_at.slice(0, 10),
      completions: completionRecords,
    };

    // Compute due dates for the current week (needed by grace/cold-start)
    const weekStart = addDays(today, -6);
    const weekEnd = addDays(today, 6);
    const dueDates = getDueDatesInRange(config, weekStart, weekEnd, context);

    // Day state
    const dayState = getHabitDayState(
      config,
      completionRecords,
      today,
      context,
      today,
      dueDates,
      firstDayOfWeek,
    );

    // Score
    const startDate = habit.created_at.slice(0, 10);
    const coldStart = isColdStart(startDate, today);
    const score = coldStart
      ? 0
      : computeScore(config, completionRecords, startDate, today, firstDayOfWeek);

    // Cold start display
    const coldStartDisplay = coldStart
      ? getColdStartDisplay(completionRecords, dueDates, today, firstDayOfWeek)
      : null;

    // Period progress
    let periodProgress: PeriodProgress | null = null;
    if (config.type === "x_per_week" || config.type === "x_per_month") {
      const bounds = getPeriodBounds(config, today, firstDayOfWeek);
      if (bounds) {
        periodProgress = getPeriodProgress(config.count, completionRecords, bounds, today);
      }
    }

    // Current completion for today
    const completion = habitCompletions.find((c) => c.effective_date === today) ?? null;

    // Effective target (light day halves count targets)
    const effectiveTarget = getEffectiveTarget(habit.type, habit.target_count, isLightDayToday);

    return {
      habit,
      dayState,
      score,
      isColdStart: coldStart,
      coldStartDisplay,
      periodProgress,
      completion,
      effectiveTarget,
      category: habit.category_id ? (categoryMap.get(habit.category_id) ?? null) : null,
    };
  });

  // Group by time_of_day
  const blockMap = new Map<TimeOfDay, HabitDisplay[]>();
  for (const block of BLOCK_ORDER) {
    blockMap.set(block, []);
  }
  for (const hd of allHabits) {
    const list = blockMap.get(hd.habit.time_of_day);
    if (list) {
      list.push(hd);
    }
  }

  const blocks: TimeBlock[] = BLOCK_ORDER.map((name) => {
    const blockHabits = blockMap.get(name) ?? [];
    return {
      name,
      label: BLOCK_LABELS[name],
      habits: blockHabits,
      completed: blockHabits.filter((h) => h.dayState === "done" || h.dayState === "skipped")
        .length,
      total: blockHabits.length,
    };
  }).filter((b) => b.total > 0);

  // Layout mode
  const density = settings.density;
  let layoutMode: LayoutMode;
  if (density === "lean") {
    layoutMode = "lean";
  } else if (density === "dense") {
    layoutMode = "dense";
  } else {
    layoutMode = allHabits.length > 7 ? "dense" : "lean";
  }

  // Totals
  const scheduledHabits = allHabits.filter((h) => h.dayState !== "not-scheduled");
  const totalDue = scheduledHabits.length;
  const totalDone = scheduledHabits.filter(
    (h) => h.dayState === "done" || h.dayState === "skipped",
  ).length;

  return {
    blocks,
    allHabits,
    totalDue,
    totalDone,
    layoutMode,
    today,
    currentBlock,
    isLightDay: isLightDayToday,
  };
}
